'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

const serviceLinks = [
  { label: 'AppSec & Vulnerability Management', href: '/services/appsec', tag: '[01]' },
  { label: 'Compliance & Continuous Compliance', href: '/services/compliance', tag: '[02]' },
  { label: 'Threat Modelling & Architecture', href: '/services/threat-modelling', tag: '[03]' },
  { label: 'Continuous DevSecOps', href: '/services/devsecops', tag: '[04]' },
  { label: 'IaC & Pipeline Security', href: '/services/iac-pipeline', tag: '[05]' },
  { label: 'Cloud & Platform Engineering', href: '/services/cloud-platform', tag: '[06]' },
  { label: 'Penetration Testing', href: '/services/pentesting', tag: '[07]' },
  { label: 'AI Penetration Testing', href: '/services/ai-pentesting', tag: '[08]' },
];

const programmeLinks = [
  { label: 'Accelerator Programme', href: '/services/accelerator', tag: '[A]', desc: 'Rapid audit readiness & remediation' },
  { label: 'Bespoke Security Programmes', href: '/services/security-programmes', tag: '[B]', desc: 'Custom programmes mapped to frameworks' },
  { label: 'Security Focus Programme', href: '/services/security-focus', tag: '[C]', desc: 'Auditor aligned. No unnecessary work.' },
  { label: 'IaC Security Programme', href: '/services/iac-programme', tag: '[D]', desc: 'Infrastructure security built as a programme' },
];

const industryLinks = [
  { label: 'Financial Services', href: '/industries/financial-services', tag: '[FS]', desc: 'FCA, PCI-DSS, DORA compliance' },
  { label: 'HealthTech & MedTech', href: '/industries/healthtech', tag: '[HT]', desc: 'HIPAA, ISO 27001, data governance' },
  { label: 'SaaS & Technology', href: '/industries/saas-tech', tag: '[ST]', desc: 'SOC 2, AppSec, DevSecOps at scale' },
  { label: 'Critical Infrastructure', href: '/industries/critical-infrastructure', tag: '[CI]', desc: 'NIS2, OT/ICS security, resilience' },
  { label: 'Telecom', href: '/industries/telecom', tag: '[TC]', desc: 'TSA, NIS2, CNI security obligations' },
  { label: 'FinTech & Payments', href: '/industries/fintech', tag: '[FT]', desc: 'PCI-DSS, PSD2, Open Banking security' },
  { label: 'AI & Data', href: '/industries/ai-data', tag: '[AI]', desc: 'EU AI Act, LLM security, MLOps' },
];

const aboutLinks = [
  { label: 'About TechSonar', href: '/about', desc: 'Our mission, approach, and team' },
  { label: 'How We Work', href: '/about#how-we-work', desc: 'Embedded, continuous, senior-led' },
  { label: 'Careers', href: '/about#careers', desc: 'Join the team' },
  { label: 'Partners', href: '/partners', desc: 'Who we work with' },
];

type DropdownKey = 'services' | 'programmes' | 'industries' | 'about' | null;

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [mobileExpanded, setMobileExpanded] = useState<Record<string, boolean>>({});
  const navRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (key: DropdownKey) => {
    setActiveDropdown(activeDropdown === key ? null : key);
  };

  const toggleMobile = (key: string) => {
    setMobileExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const ChevronIcon = ({ open }: { open: boolean }) => (
    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" className={`opacity-60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
      <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#050a0f]/95 backdrop-blur-sm border-b border-[#222]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-[60px]" ref={navRef}>
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2 flex-shrink-0">
          <span className="font-heading text-[#f5f5f8] text-xl font-medium tracking-wider uppercase">
            TECHSONAR
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-0">

          {/* Services dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('services')}
              className={`flex items-center gap-1 px-5 py-2 text-sm font-sans transition-colors duration-200 uppercase tracking-wide ${activeDropdown === 'services' ? 'text-[#80edfa]' : 'text-[#f5f5f8] hover:text-[#80edfa]'}`}
            >
              Services
              <ChevronIcon open={activeDropdown === 'services'} />
            </button>
            {activeDropdown === 'services' && (
              <div className="absolute top-full left-0 mt-1 w-[340px] bg-[#0a0f14] border border-[#222] shadow-2xl">
                <div className="p-2">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.href}
                      href={s.href}
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest w-8 flex-shrink-0">{s.tag}</span>
                      <span className="text-xs text-[#aaa] group-hover:text-[#f5f5f8] transition-colors font-sans uppercase tracking-wide">{s.label}</span>
                    </Link>
                  ))}
                  <div className="border-t border-[#222] mt-2 pt-2">
                    <Link
                      href="/services"
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center justify-between px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="text-xs text-[#555] group-hover:text-[#80edfa] transition-colors font-mono-brand uppercase tracking-widest">View all services</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-[#333] group-hover:text-[#80edfa] transition-colors">
                        <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Programmes dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('programmes')}
              className={`flex items-center gap-1 px-5 py-2 text-sm font-sans transition-colors duration-200 uppercase tracking-wide ${activeDropdown === 'programmes' ? 'text-[#80edfa]' : 'text-[#f5f5f8] hover:text-[#80edfa]'}`}
            >
              Programmes
              <ChevronIcon open={activeDropdown === 'programmes'} />
            </button>
            {activeDropdown === 'programmes' && (
              <div className="absolute top-full left-0 mt-1 w-[380px] bg-[#0a0f14] border border-[#222] shadow-2xl">
                <div className="p-2">
                  <div className="px-4 pt-3 pb-2">
                    <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest">Structured engagements</p>
                  </div>
                  {programmeLinks.map((p) => (
                    <Link
                      key={p.href}
                      href={p.href}
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest w-8 flex-shrink-0 mt-0.5">{p.tag}</span>
                      <div>
                        <p className="text-xs text-[#aaa] group-hover:text-[#f5f5f8] transition-colors font-sans uppercase tracking-wide">{p.label}</p>
                        <p className="text-[0.65rem] text-[#444] group-hover:text-[#666] transition-colors mt-0.5">{p.desc}</p>
                      </div>
                    </Link>
                  ))}
                  <div className="border-t border-[#222] mt-2 pt-2">
                    <Link
                      href="/programmes"
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-center justify-between px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="text-xs text-[#555] group-hover:text-[#80edfa] transition-colors font-mono-brand uppercase tracking-widest">View all programmes</span>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-[#333] group-hover:text-[#80edfa] transition-colors">
                        <path d="M2 6H10M7 3L10 6L7 9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Industries dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('industries')}
              className={`flex items-center gap-1 px-5 py-2 text-sm font-sans transition-colors duration-200 uppercase tracking-wide ${activeDropdown === 'industries' ? 'text-[#80edfa]' : 'text-[#f5f5f8] hover:text-[#80edfa]'}`}
            >
              Industries
              <ChevronIcon open={activeDropdown === 'industries'} />
            </button>
            {activeDropdown === 'industries' && (
              <div className="absolute top-full left-0 mt-1 w-[380px] bg-[#0a0f14] border border-[#222] shadow-2xl">
                <div className="p-2">
                  <div className="px-4 pt-3 pb-2">
                    <p className="font-mono-brand text-[0.6rem] text-[#444] uppercase tracking-widest">Sector expertise</p>
                  </div>
                  {industryLinks.map((ind) => (
                    <Link
                      key={ind.href}
                      href={ind.href}
                      onClick={() => setActiveDropdown(null)}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest w-8 flex-shrink-0 mt-0.5">{ind.tag}</span>
                      <div>
                        <p className="text-xs text-[#aaa] group-hover:text-[#f5f5f8] transition-colors font-sans uppercase tracking-wide">{ind.label}</p>
                        <p className="text-[0.65rem] text-[#444] group-hover:text-[#666] transition-colors mt-0.5">{ind.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* About dropdown */}
          <div className="relative">
            <button
              onClick={() => toggleDropdown('about')}
              className={`flex items-center gap-1 px-5 py-2 text-sm font-sans transition-colors duration-200 uppercase tracking-wide ${activeDropdown === 'about' ? 'text-[#80edfa]' : 'text-[#f5f5f8] hover:text-[#80edfa]'}`}
            >
              About
              <ChevronIcon open={activeDropdown === 'about'} />
            </button>
            {activeDropdown === 'about' && (
              <div className="absolute top-full left-0 mt-1 w-[300px] bg-[#0a0f14] border border-[#222] shadow-2xl">
                <div className="p-2">
                  {aboutLinks.map((a) => (
                    <Link
                      key={a.href}
                      href={a.href}
                      onClick={() => setActiveDropdown(null)}
                      className="flex flex-col px-4 py-3 hover:bg-[#181b1f] transition-colors group"
                    >
                      <span className="text-xs text-[#aaa] group-hover:text-[#f5f5f8] transition-colors font-sans uppercase tracking-wide">{a.label}</span>
                      <span className="text-[0.65rem] text-[#444] group-hover:text-[#666] transition-colors mt-0.5">{a.desc}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

        </nav>

        {/* Right Actions */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            href="/contact"
            className="btn-primary text-xs"
          >
            Contact Us
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden text-[#f5f5f8] p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <div className="w-6 flex flex-col gap-1.5">
            <span className={`block h-px bg-current transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block h-px bg-current transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-px bg-current transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-[#050a0f] border-t border-[#222] px-6 py-6">

          {/* Services */}
          <div className="border-b border-[#222]">
            <button
              onClick={() => toggleMobile('services')}
              className="flex items-center justify-between w-full py-3 text-[#f5f5f8] text-sm uppercase tracking-wide hover:text-[#80edfa] transition-colors"
            >
              Services
              <ChevronIcon open={!!mobileExpanded['services']} />
            </button>
            {mobileExpanded['services'] && (
              <div className="pb-3 pl-4 flex flex-col gap-1">
                {serviceLinks.map((s) => (
                  <Link
                    key={s.href}
                    href={s.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-[#aaa] hover:text-[#80edfa] transition-colors"
                  >
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest">{s.tag}</span>
                    <span className="text-xs font-sans uppercase tracking-wide">{s.label}</span>
                  </Link>
                ))}
                <Link href="/services" onClick={() => setMobileOpen(false)} className="py-2 text-xs text-[#555] hover:text-[#80edfa] transition-colors font-mono-brand uppercase tracking-widest">
                  View all services →
                </Link>
              </div>
            )}
          </div>

          {/* Programmes */}
          <div className="border-b border-[#222]">
            <button
              onClick={() => toggleMobile('programmes')}
              className="flex items-center justify-between w-full py-3 text-[#f5f5f8] text-sm uppercase tracking-wide hover:text-[#80edfa] transition-colors"
            >
              Programmes
              <ChevronIcon open={!!mobileExpanded['programmes']} />
            </button>
            {mobileExpanded['programmes'] && (
              <div className="pb-3 pl-4 flex flex-col gap-1">
                {programmeLinks.map((p) => (
                  <Link
                    key={p.href}
                    href={p.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-[#aaa] hover:text-[#80edfa] transition-colors"
                  >
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest">{p.tag}</span>
                    <span className="text-xs font-sans uppercase tracking-wide">{p.label}</span>
                  </Link>
                ))}
                <Link href="/programmes" onClick={() => setMobileOpen(false)} className="py-2 text-xs text-[#555] hover:text-[#80edfa] transition-colors font-mono-brand uppercase tracking-widest">
                  View all programmes →
                </Link>
              </div>
            )}
          </div>

          {/* Industries */}
          <div className="border-b border-[#222]">
            <button
              onClick={() => toggleMobile('industries')}
              className="flex items-center justify-between w-full py-3 text-[#f5f5f8] text-sm uppercase tracking-wide hover:text-[#80edfa] transition-colors"
            >
              Industries
              <ChevronIcon open={!!mobileExpanded['industries']} />
            </button>
            {mobileExpanded['industries'] && (
              <div className="pb-3 pl-4 flex flex-col gap-1">
                {industryLinks.map((ind) => (
                  <Link
                    key={ind.href}
                    href={ind.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 py-2 text-[#aaa] hover:text-[#80edfa] transition-colors"
                  >
                    <span className="font-mono-brand text-[0.6rem] text-[#80edfa] uppercase tracking-widest">{ind.tag}</span>
                    <span className="text-xs font-sans uppercase tracking-wide">{ind.label}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* About */}
          <div className="border-b border-[#222]">
            <button
              onClick={() => toggleMobile('about')}
              className="flex items-center justify-between w-full py-3 text-[#f5f5f8] text-sm uppercase tracking-wide hover:text-[#80edfa] transition-colors"
            >
              About
              <ChevronIcon open={!!mobileExpanded['about']} />
            </button>
            {mobileExpanded['about'] && (
              <div className="pb-3 pl-4 flex flex-col gap-1">
                {aboutLinks.map((a) => (
                  <Link
                    key={a.href}
                    href={a.href}
                    onClick={() => setMobileOpen(false)}
                    className="py-2 text-xs text-[#aaa] hover:text-[#80edfa] transition-colors font-sans uppercase tracking-wide"
                  >
                    {a.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            <Link href="/contact" onClick={() => setMobileOpen(false)} className="btn-primary text-center justify-center">
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
